import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge, ConfidenceBadge, kindIcon } from "@/components/StatusBadge";
import { Loader2, CheckCircle2, Send, AlertTriangle } from "lucide-react";

const TABS = [
  { key: "all", label: "All" },
  { key: "needs_review", label: "Needs Review" },
  { key: "processing", label: "Processing" },
  { key: "approved", label: "Approved" },
  { key: "published", label: "Published" },
];

export default function Review() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const [{ data: queue }, { data: pub }] = await Promise.all([
      api.get("/review/queue"),
      api.get("/uploads?status=published&limit=200"),
    ]);
    const map = {};
    [...queue, ...pub].forEach((f) => (map[f.id] = f));
    setItems(Object.values(map).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      if (action === "approve") await api.patch(`/uploads/${id}`, { status: "approved" });
      else await api.post(`/uploads/${id}/${action}`);
      toast.success(`Item ${action}ed`);
      await load();
    } catch (e) { toast.error("Action failed"); }
    finally { setBusy(null); }
  };

  if (!items) return <div className="flex justify-center h-96 items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const filtered = tab === "all" ? items : items.filter((i) => i.status === tab);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-white tracking-tight">Review & Publish</h1>
        <p className="text-slate-400 mt-2">Nothing reaches the CEO view until you publish it. Review AI classifications, then approve and publish.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const cnt = t.key === "all" ? items.length : items.filter((i) => i.status === t.key).length;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`review-tab-${t.key}`}
              className={`px-4 py-2 rounded-lg text-sm border transition-colors ${tab === t.key ? "bg-blue-600/15 text-blue-300 border-blue-500/30" : "text-slate-400 border-white/10 hover:bg-white/5"}`}>
              {t.label} <span className="text-xs text-slate-600">{cnt}</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-white/5 bg-[#111827] p-16 text-center text-slate-500">Nothing here.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((f) => (
            <div key={f.id} data-testid={`review-item-${f.id}`} className="rounded-xl border border-white/5 bg-[#111827] p-4 flex flex-col md:flex-row md:items-center gap-4">
              <Link to={`/file/${f.id}`} className="flex items-center gap-3 min-w-0 flex-1">
                <div className="text-blue-400 flex-shrink-0">{kindIcon(f.kind, "w-5 h-5")}</div>
                <div className="min-w-0">
                  <div className="text-white font-medium truncate hover:text-blue-300 transition-colors">{f.title}</div>
                  <div className="text-xs text-slate-500 truncate">{f.executive_summary || f.original_filename}</div>
                </div>
              </Link>
              <div className="flex items-center gap-2 flex-wrap">
                {f.sensitive_flags?.length > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-red-400"><AlertTriangle className="w-3 h-3" />sensitive</span>}
                <ConfidenceBadge value={f.confidence} />
                <StatusBadge status={f.status} />
              </div>
              <div className="flex items-center gap-2">
                {f.status !== "published" && f.status !== "processing" && (
                  <>
                    {f.status === "needs_review" && (
                      <button onClick={() => act(f.id, "approve")} disabled={busy === f.id} data-testid={`approve-${f.id}`} className="inline-flex items-center gap-1.5 text-xs border border-white/10 hover:bg-white/5 text-white rounded-lg px-3 py-1.5 transition-colors">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                      </button>
                    )}
                    <button onClick={() => act(f.id, "publish")} disabled={busy === f.id} data-testid={`publish-${f.id}`} className="inline-flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 transition-colors">
                      {busy === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Publish
                    </button>
                  </>
                )}
                {f.status === "published" && (
                  <button onClick={() => act(f.id, "unpublish")} disabled={busy === f.id} data-testid={`unpublish-${f.id}`} className="text-xs border border-white/10 hover:bg-white/5 text-slate-300 rounded-lg px-3 py-1.5 transition-colors">Unpublish</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
